// Copyright (c) 2019-24 Stax·ai, Inc.
// See LICENSE.md file in project root directory

import React from 'react';
import Loading from 'react-loading';
import { toast } from 'react-toastify';
import renderHTML from 'react-render-html';
import { Calendar } from 'react-date-range';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file

// Components
import Canvas from './Canvas';
import PageSplit from './PageSplit';

// Utilities
import getStringWidth from '../../../utilities/stringWidth';
import formatDate from '../../../utilities/formatDate';
import inputKey from '../../../utilities/inputKey';
import clamp from '../../../utilities/clamp';

// Styles
import '../../../styles/document.css';
import '../../../styles/signature.css';

export default class Page extends React.Component {

    state = {
        selectionStart: null,
        aspectRatio: 11 / 8.5,
        openComments: {},
        replyTo: null,
        replyContent: "",
        replyTags: [],
        dragImg: null,
        textHover: false,
        words: [],
        editBoundIdx: -1,
        hoveredBoundIdx: -1,
        editBound: {},
        copyBound: null,
        fillableValue: "",
        showDatePicker: false,
        showSubmit: false
    }

    // non-state vars
    mouseDown = false;
    aspectRatio = 1;

    componentDidMount = () => {
        document.addEventListener('dragover', e => {
            const tmpX = this.clientX;
            const tmpY = this.clientY;
            this.clientX = e.pageX;
            this.clientY = e.pageY;
            this.deltaX = this.clientX - tmpX;
            this.deltaY = this.clientY - tmpY;
        });
    }

    componentWillReceiveProps = (next) => {
        const n = next.newCommentId;
        if (n !== null && !this.state.openComments[n] && this.state.replyTo !== n) {
            this.setState(prev => ({
                openComments: {
                    ...prev.openComments,
                    [n]: true
                },
                replyTo: n,
                replyContent: ""
            }));
            setTimeout(() => {
                const input = document.getElementById('comment-thread-reply');
                if (input) input.focus();
            }, 300);
        }

        // update page height when new rotations/zooms come in
        if (this.props.rotation !== next.rotation)
            this.setAspectRatio();

        // Resort words if new ones arrive
        if (this.props.words !== next.words)
            this.sortWords();
    }

    sortWords = () => {
        const words = this.props.words;
        this.setState({ words });
    }

    saveReply = async () => {
        if (!this.state.replyTo || this.state.replyContent === "")
            return;

        await this.setState({ commentSaveStatus: "Saving..." });

        this.setState({ commentSaveStatus: "Saved." });
    }

    toPageCoords = (coords) => {
        if (!coords.x || !coords.y)
            return { x: 0, y: 0 };

        const pref = this.refs["page-" + this.props.pageIdx];
        if (!pref) return { x: 0, y: 0 };
        const page = pref.getBoundingClientRect();

        return {
            x: (coords.x - page.x) / page.width,
            y: (coords.y - page.y) / page.height
        };
    }

    getHoveredWordIndex = (x, y, restrictive = false) => {
        let firstNewlineIdx = null;
        let lastInlineWord = null;

        const cursorInWords = [];

        for (const [idx, word] of this.props.words.entries()) {

            // Inline cursor?
            if ((y > word.y && y - word.y < word.height)) {
                // Cursor in word
                if (Math.abs(y - word.y) < word.height && x > word.x && x - word.x < word.width) {
                    cursorInWords.push(idx);
                    continue;
                }

                // // This can cause us to jump to previous lines
                // // Cursor is before the word
                // else if (!restrictive && x < word.x)
                //     return idx-1;

                lastInlineWord = idx;
            }

            // Reached text on a line past the cursor
            else if (!restrictive && word.y - y > word.height * 2.0 && firstNewlineIdx === null)
                firstNewlineIdx = clamp(idx, 0, this.props.words.length);
        }

        // If cursor is in multiple words, then get the work with the smallest height
        if (cursorInWords.length === 1) return cursorInWords[0];
        else if (cursorInWords.length > 1) {
            let smallestWordIdx;
            let smallestWordHeight;
            for (const idx of cursorInWords) {
                const word = this.props.words[idx];
                if (!smallestWordHeight || word.height < smallestWordHeight) {
                    smallestWordHeight = word.height;
                    smallestWordIdx = idx;
                }
            }
            return smallestWordIdx;
        }

        if (!restrictive && lastInlineWord !== null)
            return lastInlineWord;

        // If the cursor was not (in a word), or (inline + before a word), find the last inline-word that reached newline
        if (!restrictive && firstNewlineIdx != null)
            return firstNewlineIdx;

        // We've run through all the words, and the cursor is after all of them...
        if (!restrictive)
            return this.props.words.length - 1;

        return null;
    }

    substrWidthPercent = (string, substring) => {
        const full = getStringWidth(string);
        const sub = getStringWidth(substring);
        if (!Number.isNaN(full) && !Number.isNaN(sub))
            return sub / full;
        return substring.length / string.length;
    }

    highlightSingle = async (e) => {
        e.preventDefault();
        const { x, y } = this.toPageCoords({ x: e.pageX, y: e.pageY });
        if (!x || !y) return;

        const wordIdx = this.getHoveredWordIndex(x, y, true);
        if (wordIdx === null) return;

        this.setState({ selectionStart: wordIdx });

        const selection = [{
            ...this.props.words[wordIdx],
            wordIdx
        }];

        // If retagging field, then call the callback function!
        if (this.props.function === "retag-field" && this.props.callback && selection && selection.length)
            await this.props.callback(selection);

        this.props.select(selection);
    }

    getSelectionStart = (x, y) => {
        let offset = 0;
        let idx = this.getHoveredWordIndex(x, y, false);

        // Get offset of cursor to word.left
        const word = this.props.words[idx];
        if (word && x > word.x)
            offset = x - word.x;

        // First letter?
        if (word && offset <= 2 * (word.width / word.text.length))
            offset = 0;

        // TODO: Fix offset highlighting with multiple lines
        // Overwrite offset for now
        offset = 0;

        var selectionStart = {
            idx: idx,
            offsetX: offset,
        }

        this.setState({ selectionStart });
    }

    applyOffsetToStart = (x, word, offset) => {
        if (!word.text)
            return

        // Cursor is ahead of starting position
        if (x >= word.x + offset) {
            const percent = offset / word.width;
            let charIdx = Math.round(word.text.length * percent);
            // if (charIdx > 0) charIdx--;
            word.text = word.text.substring(charIdx)
            word.x += offset;
            word.width -= offset;
        }

        // Cursor is behind starting position
        else {
            // Dont drag behin the original word
            x = clamp(x, word.x, word.x + offset)

            // Find relative text
            const newWidth = (word.x + offset) - x;
            const stopPercent = offset / word.width;
            var stopIdx = Math.round(word.text.length * stopPercent);
            stopIdx = clamp(stopIdx, 0, word.text.length)
            const startPercent = (x - word.x) / word.width;
            var startIdx = Math.floor(word.text.length * startPercent)
            startIdx = clamp(startIdx, 0, stopIdx)

            word.text = word.text.substring(startIdx, stopIdx)
            word.width = newWidth;
            word.x = x;
        }

        return word
    }

    highlightWords = (e) => {
        e.preventDefault();
        const { x, y } = this.toPageCoords({ x: e.pageX, y: e.pageY });
        if (!x || !y || !this.mouseDown) return;

        // Compute selection start location
        if (this.state.selectionStart === null) {
            return this.getSelectionStart(x, y);
        }

        const wordIdx = this.getHoveredWordIndex(x, y, false);
        if (!this.props.words[wordIdx])
            return;
        var word = { ...this.props.words[wordIdx] };

        // Apply offset to selection start
        if (wordIdx === this.state.selectionStart.idx)
            word = this.applyOffsetToStart(x, word, this.state.selectionStart.offsetX);

        let selection = [];

        // Case 1: Forward highlight
        if (this.state.selectionStart.idx <= wordIdx) {
            for (let i = this.state.selectionStart.idx; i < wordIdx; i++) {
                if (i === this.state.selectionStart.idx) {
                    var start = { ...this.props.words[i] };
                    start = this.applyOffsetToStart(x, start, this.state.selectionStart.offsetX);
                    selection.push({
                        ...start,
                        wordIdx: i
                    });
                    continue;
                }

                selection.push({
                    ...this.props.words[i],
                    wordIdx: i
                });
            }

            // cursor is ahead of starting position
            if (x > word.x && y >= word.y) {
                const percent = (x - word.x) / word.width;
                const charIdx = Math.round(word.text.length * percent);
                const partialString = word.text.substring(0, charIdx);
                const partialWidth = this.substrWidthPercent(word.text, partialString) * word.width;
                selection.push({
                    ...word,
                    width: percent > 1 ? word.width : partialWidth,
                    text: partialString,
                    wordIdx
                });
            }

            // cursor is behind starting position
            else if (wordIdx === this.state.selectionStart.idx) {
                selection.push({
                    ...word,
                    wordIdx
                });
            }

        }

        // Case 2: Reverse highlight
        else {
            for (let i = this.state.selectionStart.idx; i >= wordIdx; i--)
                selection.unshift({
                    ...this.props.words[i],
                    wordIdx: i
                });

            // const percent = (word.x + word.width - x) / word.width;
            // const charIdx = Math.floor(word.text.length * percent);
            // const partialString = word.text.substring(word.text.length - charIdx);
            // const partialWidth = this.substrWidthPercent(word.text, partialString) * word.width;

            // selection.push({
            //     ...word,
            //     text: partialString,
            //     width: percent > 1 ? word.width: partialWidth,
            //     x: percent > 1 ? word.x : word.x + word.width - partialWidth,
            //     wordIdx: i
            // });
        }

        this.props.select(selection);
    }

    summarizeHighlightedLine = (curLine) => {
        if (curLine.length < 1)
            return;
        const left = curLine[0].x;
        const width = curLine[curLine.length - 1].x + curLine[curLine.length - 1].width - left;
        const height = curLine.reduce((sum, w) => sum + w.height, 0) / curLine.length;
        const top = curLine.reduce((sum, w) => sum + w.y, 0) / curLine.length;
        return ({ left, top, width, height });
    }

    dragBoundsCorner = (corner, e) => {
        const pref = this.refs["page-" + this.props.pageIdx];
        if (!pref) return <></>;
        const page = pref.getBoundingClientRect();

        if (Math.abs(this.deltaX) > 20 || Math.abs(this.deltaY) > 20)
            return;

        const x = (this.clientX - page.left) / page.width;
        const y = (this.clientY - page.top) / page.height;

        let editBound = { ...this.state.editBound };
        if (!editBound) return;

        // Control each side independently
        if (corner.includes('top')) {
            editBound.height += editBound.y - y
            editBound.y = y;
        }
        else
            editBound.height = y - editBound.y;

        if (corner.includes('left')) {
            editBound.width += editBound.x - x;
            editBound.x = x;
        }
        else
            editBound.width = x - editBound.x;

        this.setState({ editBound });
    }

    insertBound = async (x, y, width, height, label = "") => {
        const ctrl = this.props.ctrl;
        const newBound = {
            label,
            data: {},
            page: this.props.pageIdx,
            x,
            y,
            width,
            height
        };
        const newBoundIdx = this.props.bounds.length;
        await this.props.setBounds([
            ...this.props.bounds,
            newBound
        ]);

        // If user is holding down the control key, then don't select the new bound
        if (!ctrl) {
            await this.setState({
                editBound: newBound,
                editBoundIdx: newBoundIdx
            });
            this.props.setTool("");
        }
    }

    pasteBound = async (x, y) => {
        const b = this.state.copyBound;
        if (!b)
            return;

        this.insertBound(
            x - b.width / 2,
            y - b.height / 2,
            b.width,
            b.height,
            b.label
        );
    }

    saveBounds = async (bounds) => {
        await this.props.setBounds(bounds);
        this.setState({
            editBound: null,
            editBoundIdx: -1,
            hoveredBoundIdx: -1
        });
    }

    renderHighlightedWords = () => {

        if (this.props.selection.length === 0)
            return <></>;

        // Aggregate into lines
        let lines = [];
        // let curLine = [];
        // let prev = this.props.selection[0];

        for (const word of this.props.selection) {
            // // Same line?
            // if (word.y <= prev.y + prev.height) {
            //     curLine.push(word);
            //     prev = word;
            // }

            // else {
            //     // Summarize line
            //     const summarizedLine = this.summarizeHighlightedLine(curLine);
            //     if (summarizedLine) lines.push(summarizedLine);
            //     prev = word;
            //     curLine = [word];
            // }

            // Highlight each word
            lines.push({
                left: word.x,
                top: word.y,
                width: word.width,
                height: word.height
            });
        }

        // Summarize last line
        // if (curLine.length > 0) {
        //     const summarizedLine = this.summarizeHighlightedLine(curLine);
        //         if (summarizedLine) lines.push(summarizedLine);
        // }

        const pref = this.refs["page-" + this.props.pageIdx];
        if (!pref) return <></>;
        const page = pref.getBoundingClientRect();

        return lines.map((line, idx) => <div
            key={idx}
            className="document-selection"
            style={{
                left: (page.width * line.left - 2) + 'px',
                top: (page.height * line.top - 2) + 'px',
                width: (page.width * line.width + 4) + 'px',
                height: (page.height * line.height + 4) + 'px'
            }}

            // No pointer events on highlighted words!
            onMouseUp={() => {
                this.setState({ textHover: true });
                this.resetSelection()
            }}
            onMouseDown={(e) => {
                this.setState({ textHover: true });
                this.resetSelection()
            }}

            // Get text cursor
            onMouseMove={(e) => {
                this.setState({ textHover: true });
                this.highlightWords(e)
            }}
        />);

    }

    overTextCursor = (e) => {
        const { x, y } = this.toPageCoords({ x: e.pageX, y: e.pageY });
        if (!x || !y) return;

        const wordIdx = this.getHoveredWordIndex(x, y, true);
        const word = this.props.words[wordIdx];
        if (!word) {
            // Set cursor to normal
            this.setState({ textHover: false });
            return;
        }

        // Over a word, show text selection cursor
        this.setState({ textHover: true });
    }


    renderSearchResults = () => {

        let q = this.props.query;
        if (!q || q.length < 3)
            return <></>;

        const mws = q[0] === "*";
        // if (mws) q = q.substring(1);

        const pref = this.refs["page-" + this.props.pageIdx];
        if (!pref) return <></>;
        const page = pref.getBoundingClientRect();

        let results = [];
        const qWords = q.trim().split(" ").length;

        // Multi-word search compilation
        if (mws)
            for (const word of this.props.matches)
                results.push([word]);

        else {

            // Group the results by phrase
            let group = [];
            for (const [wordIdx, word] of this.props.matches.entries()) {
                // Start of phrase?
                if (wordIdx % qWords === 0) {
                    if (group.length > 0) // Compile previous
                        results.push(group);

                    group = [word]; // New phrase
                }

                else group.push(word); // Append to existing phrase group   
            }

            if (group.length > 0) // Compile last
                results.push(group);

        }

        return results.map((words, idx) => {

            if (words.length === 0) return <></>;

            // Aggregate
            const aggText = words.map(w => w.text).join(" ");
            const aggWidth = words.reduce((sum, word, wIdx) => sum + word.width + (wIdx > 0 ? word.x - (words[wIdx - 1].x + words[wIdx - 1].width) : 0), 0);

            // Average
            const avgHeight = words.reduce((sum, word) => sum + word.height, 0) / words.length;
            const avgY = words.reduce((sum, word) => sum + word.y, 0) / words.length;

            const startIdx = aggText.toLowerCase().indexOf(q.toLowerCase());
            const offsetX = this.substrWidthPercent(aggText, aggText.substring(0, startIdx)) * aggWidth;
            const subWidth = this.substrWidthPercent(aggText, aggText.substring(startIdx, startIdx + q.length)) * aggWidth;

            return (<div
                key={idx}
                id={"document-match-" + words[0].matchIdx}
                className="document-selection document-matches"
                style={{
                    backgroundColor: this.props.matchIdx === words[0].matchIdx ? "var(--color-results-focus)" : "var(--color-results)",
                    borderBottom: this.props.matchIdx === words[0].matchIdx ? "2px solid var(--color-primary)" : "none",
                    left: (page.width * (words[0].x + offsetX) - 2) + 'px',
                    top: (page.height * avgY - 4) + 'px',
                    width: (subWidth * page.width + 4) + 'px',
                    height: (page.height * avgHeight + 8) + 'px'
                }}
            />);

        });

    }

    renderBoundingBoxes = () => {
        const bounds = this.props.bounds;
        const pref = this.refs["page-" + this.props.pageIdx];
        if (!pref) return <></>;
        const page = pref.getBoundingClientRect();

        if (!bounds || bounds.length === 0)
            return <></>;

        return bounds.map((b, idx) => {

            if (b.page !== this.props.pageIdx)
                return "";

            // Editable?
            if (this.state.editBoundIdx === idx) {
                const eb = this.state.editBound;
                const left = (page.width * eb.x - 2) + 'px';
                const top = (page.height * eb.y - 2);

                return <React.Fragment key={idx}>
                    {/* Label */}
                    <div
                        className="document-page-bound-edit-label"
                        style={{
                            left,
                            top: top - 30,
                            minWidth: page.width * eb.width + 8
                        }}
                    >
                        <input
                            className="document-page-bound-edit-label-input"
                            type="text"
                            value={eb.label.toUpperCase()}
                            onChange={e => this.setState({
                                editBound: {
                                    ...this.state.editBound,
                                    label: e.target.value.toLowerCase()
                                }
                            })}
                            onKeyDown={(e) => inputKey(e, {
                                "Enter": () => {
                                    let bounds = [...this.props.bounds];
                                    bounds[idx] = this.state.editBound;
                                    this.saveBounds(bounds);
                                }
                            })}
                            placeholder="Enter label..."
                        />
                    </div>

                    <div
                        className="document-page-bound-edit"
                        style={{
                            left,
                            top,
                            width: page.width * eb.width + 4,
                            height: page.height * eb.height + 4
                        }}
                    >
                        <div className="signature-editor-controls">
                            {/* Draggable corners */}
                            <div
                                onDrag={e => this.dragBoundsCorner('top-left', e)}
                                className="signature-editor-corner signature-editor-corner-top-left"
                                draggable={true}
                            />
                            <div
                                onDrag={e => this.dragBoundsCorner('top-right', e)}
                                className="signature-editor-corner signature-editor-corner-top-right"
                                draggable={true}
                            />
                            <div
                                onDrag={e => this.dragBoundsCorner('bottom-left', e)}
                                className="signature-editor-corner signature-editor-corner-bottom-left"
                                draggable={true}
                            />
                            <div
                                onDrag={e => this.dragBoundsCorner('bottom-right', e)}
                                className="signature-editor-corner signature-editor-corner-bottom-right"
                                draggable={true}
                            />

                            {/* Copy */}
                            <img
                                className="signature-editor-control-copy"
                                src="https://static.stax.ai/images/icons/copy.png"
                                alt="(-)"
                                title="Copy bounds"
                                onClick={async () => {
                                    // await this.props.loading(1005);
                                    // let bounds = [ ...this.props.bounds ];
                                    // bounds.splice(idx, 1);
                                    // this.saveBounds(bounds);
                                    // setTimeout(() => this.props.loaded(), 500);
                                    const b = this.props.bounds[idx];
                                    this.setState({
                                        copyBound: {
                                            width: b.width,
                                            height: b.height,
                                            label: b.label
                                        },
                                        editBound: null,
                                        editBoundIdx: -1,
                                        hoveredBoundIdx: -1
                                    });
                                    this.props.setTool("CopyBox");
                                    toast.info("Click on where you'd like the center of the bounds to be copied to.");
                                }}
                            />

                            {/* Clear */}
                            <img
                                className="signature-editor-control-clear"
                                src="https://static.stax.ai/images/icons/remove.png"
                                alt="(-)"
                                title="Delete bounds"
                                onClick={async () => {
                                    await this.props.loading(1005);
                                    let bounds = [...this.props.bounds];
                                    bounds.splice(idx, 1);
                                    this.saveBounds(bounds);
                                    setTimeout(() => this.props.loaded(), 500);
                                }}
                            />

                            {/* Save */}
                            <img
                                className="signature-editor-control-bake"
                                src="https://static.stax.ai/images/icons/reviewed.png"
                                alt="(+)"
                                title="Save"
                                onClick={() => {
                                    let bounds = [...this.props.bounds];
                                    bounds[idx] = this.state.editBound;
                                    this.saveBounds(bounds);
                                }}
                            />
                        </div>
                    </div>
                </React.Fragment>;
            }

            // Static
            else {

                const left = (page.width * b.x - 2) + 'px';
                const top = (page.height * b.y - 2);

                return <React.Fragment key={idx}>
                    <div
                        className="document-page-bound"
                        style={{
                            left,
                            top,
                            width: page.width * b.width + 4,
                            height: page.height * b.height + 4,
                            pointerEvents: this.state.editBoundIdx >= 0 || ["InsertBox", "CopyBox"].includes(this.props.tool) ? 'none' : 'all'
                        }}
                        onMouseEnter={() => this.setState({ hoveredBoundIdx: idx })}
                        onMouseLeave={() => this.setState({ hoveredBoundIdx: -1 })}
                    />
                    <div
                        className="document-hover-tag-key document-page-bound-label"
                        style={{
                            left,
                            top: top - 12
                        }}
                    >{b.label.toUpperCase()}</div>
                    <img
                        src="https://static.stax.ai/images/icons/edit-inverse.png"
                        className={"document-page-bound-img" + (this.state.hoveredBoundIdx === idx ? "" : " document-page-bound-img-hide")}
                        title="Click to edit bounding box"
                        style={{
                            left: page.width * (b.x + b.width) - 19,
                            top: top + 3
                        }}
                        alt="[Edit]"
                        onClick={() => this.setState({
                            editBoundIdx: idx,
                            editBound: { ...b }
                        })}
                        onMouseEnter={() => this.setState({ hoveredBoundIdx: idx })}
                        onMouseLeave={() => this.setState({ hoveredBoundIdx: -1 })}
                    />
                </React.Fragment>
            }
        });
    }

    renderHoveredElement = (elem) => {
        if (!elem) return <></>;
        const pref = this.refs["page-" + this.props.pageIdx];
        if (!pref) return <></>;
        const page = pref.getBoundingClientRect();

        // Tagged field (highlight words)
        if (elem.tag && ((elem.tag.pages && elem.tag.pages.length === 1 && elem.tag.pages[0] === this.props.pageIdx) || (this.props.pageIdx === 0 && (!elem.tag.pages || elem.tag.pages.length === 0))) && elem.tag.tags && elem.tag.tags.length > 0) {
            const tl = this.props.words[elem.tag.tags[0]];
            const br = this.props.words[elem.tag.tags[elem.tag.tags.length - 1]];
            if (!tl || !br)
                return <></>;
            const left = (page.width * (tl.x) - 4) + 'px';
            const top = page.height * (tl.y) - 4;
            return <>
                <div
                    className="document-hover document-hover-tag"
                    style={{
                        left,
                        top: top + 'px',
                        width: (page.width * (br.x + br.width - tl.x) + 4) + 'px',
                        height: (page.height * (br.y + br.height - tl.y) + 4) + 'px'
                    }}
                />
                <div
                    className="document-hover-tag-key"
                    style={{
                        left,
                        top: (top - 12) + 'px'
                    }}
                >{elem.tag.key}</div>
            </>;
        }

        return <></>;
    }

    renderFillableInputs = () => {
        const pref = this.refs["page-" + this.props.pageIdx];
        if (!pref) return <></>;
        const page = pref.getBoundingClientRect();

        if (!this.props.inputs) return <></>;

        return this.props.inputs.map((f, idx) => {
            const left = (page.width * f.x) + 'px';
            const top = (page.height * f.y) + 'px';
            const hovered = this.props.hovered && this.props.hovered.input && this.props.hovered.input.idx === f.idx;
            return <React.Fragment key={idx}>
                <div
                    className={"document-hover document-hover-tag" + (hovered ? " document-hover-tag-accent" : "")}
                    style={{
                        left,
                        top,
                        width: (page.width * f.width) + 'px',
                        height: (page.height * f.height) + 'px'
                    }}
                />
                <div
                    className={"document-hover-tag-key" + (hovered ? " document-hover-tag-key-accent" : "")}
                    style={{ left, top }}
                >{f.key}</div>
            </React.Fragment>;
        });
    }

    renderFillablePublic = () => {
        if (!this.props.fillable || !this.props.metadata) return <></>;

        const pref = this.refs["page-" + this.props.pageIdx];
        if (!pref) return <></>;
        const page = pref.getBoundingClientRect();

        const m = this.props.metadata;
        let checkboxLeft = 52;
        let checkboxHeight = 20;
        let checkboxWidth = 20;

        return this.props.fillable.map((f, idx) => {
            if (!('x' in f) || !('y' in f) || !f.width || !f.height || f.page !== this.props.pageIdx) return <React.Fragment key={idx}></React.Fragment>;

            const left = page.width * f.x - 4;
            const top = page.height * f.y;
            const width = page.width * f.width + 4 - (this.props.stage === idx || ["signature (Mouse Only)", "signature", "today"].includes(f.dataType) ? 0 : 10);
            const height = page.height * f.height;

            // Dynamically calculate fontSize to force text to fit inside width
            let fontSize = Math.min(15 * this.props.zoom, height);
            if (f.dataType === "signature" && this.props.stage !== idx) {
                const signature = this.props.signatures.find(s => s.fieldIdx === idx);
                if (signature && signature.data.typed)
                    fontSize = Math.min(fontSize, width / signature.data.typed.length / 0.72);
            }

            let value = m[f.key] ? String(m[f.key]) : null;
            if (value && ["date", "today"].includes(f.dataType))
                value = formatDate(value, false, true);

            else if (value && value.length > 0 && f.dataType !== "signature")
                fontSize = Math.min(fontSize, width / value.length / 0.56);

            fontSize = Math.floor(fontSize);
            const lh = Math.max(fontSize, (2 * (height - fontSize / 2)));
            const lineHeight = lh + 'px';

            const style = { left, top, width, height, fontSize, lineHeight };

            // Input?
            if (this.props.stage === idx) {
                // By input type
                let input;
                switch (f.dataType) {
                    case "textarea":
                        input = <></>; // TODO @Naru
                        break;

                    case "date":
                        input = <>
                            <div
                                className="document-fillable-input document-fillable-input-date"
                                id={"document-fillable-" + String(idx)}
                                style={{
                                    ...style,
                                    lineHeight: (height + 3) + 'px'
                                }}
                                onClick={() => this.setState({ showDatePicker: !this.state.showDatePicker })}
                            >Select date</div>
                            {this.state.showDatePicker ? <div
                                className="document-fillable-date-picker"
                                style={{
                                    left: f.x < (1 - f.x - f.width) ? left - 10 : left + width - 370,
                                    top: top + height + 4
                                }}
                            >
                                <Calendar
                                    date={this.props.value ? new Date(this.props.value) : new Date()}
                                    months={1}
                                    direction="horizontal"
                                    editableDateInputs={true}
                                    onChange={(value) => {
                                        this.props.fill(f.key, formatDate(value, false, true));
                                        this.setState({ showDatePicker: false });
                                    }}
                                />
                            </div> : ""}
                        </>;
                        break;

                    case "checkbox":
                        input = 

                            // <Checkbox
                            //     style={{
                            //         ...style,
                            //         left: left + 5,
                            //         width: width - 10  
                            //     }}
                            //     // label="Secured Link"
                            //     value={"true"}
                            //     checked={this.state.fillableValue === "true"}
                            //     onToggle={() => this.props.fill(f.key, value, false)}
                            // />; // TODO @Naru
                            <label
                                className={"checkbox"}
                                title={this.props.tooltip ? this.props.tooltip : this.props.label}
                                style={{height: checkboxHeight, width: checkboxWidth, top: top + height / 2 - 15 - 1082, left: checkboxLeft}}
                                onContextMenu={this.props.onContextMenu ? this.props.onContextMenu : () => {}}

                            >
                                <input
                                    type="checkbox"
                                    checked={this.props.checked}
                                    onChange={() => this.props.fill(f.key, "true", false)}
                                    disabled={this.props.disabled ? true : false}
                                />
                                <span className="checkmark" />
                            </label>

                        break;

                    case "signature":
                        input = <div
                            className="document-fillable-signature-click"
                            id={"document-fillable-" + String(idx)}
                            style={{
                                ...style,
                                left: left + 2,
                                width: width - 10,
                                lineHeight: (height + 3) + 'px'
                            }}
                            onClick={() => this.props.modal("collect-signature", {
                                sign: (data) => this.props.sign(data)
                            })}
                        >Click to sign</div>;
                        break;

                    case "signatureMouseOnly":
                        input = <div
                            className="document-fillable-signature-click"
                            id={"document-fillable-" + String(idx)}
                            style={{
                                ...style,
                                left: left + 5,
                                width: width - 10,
                                lineHeight: (height + 3) + 'px'
                            }}
                            onClick={() => this.props.modal("collect-signature", {
                                sign: (data) => this.props.sign(data),
                                mouseOnly: true,
                            })}
                        >Click to sign</div>;
                        break;

                    default: // Textbox
                    
                        {
                            input = <input
                                type="text"
                                className="document-fillable-input"
                                id={"document-fillable-" + String(idx)}
                                style={{
                                    ...style,
                                    left: left + 5,
                                    width: width - 10
                                }}
                                value={this.state.fillableValue}
                                onChange={e => this.setState({ fillableValue: e.target.value })}
                                onKeyDown={ (e) => {
                                    if (e.key === "Enter") {
                                        const value = this.state.fillableValue;
                                        if ((value.trim().length > 0 && this.props.stage < 9) || (this.props.stage >= 9)){
                                            this.props.fill(f.key, value, false);

                                            if (this.props.stage < this.props.fillable.length - 1) {
                                                this.setState( {fillableValue: ""});

                                            }
                                            else {
                                                this.setState({ showSubmit: true});

                                            }
                                        }
                                    }
                                }}
                            />;
                        }
                        
                };

                return <React.Fragment key={idx}>
                    {input}
                    <div
                        className="document-fillable-indicator"
                        style={{ top: top + height / 2 - 15 }}
                    >Type here & hit enter...</div>
                    <div
                        className="document-fillable-indicator-right"
                        style={{ top: top + height / 2 - 15 }}
                    />
                </React.Fragment>;

                
            }

            // Does value exist?
            if (!value && f.dataType !== "signature")
                return <React.Fragment key={idx}></React.Fragment>;

            // Else, display filled value
            switch (f.dataType) {
                // case "textarea":
                //     return <></>; // TODO @Naru

                // case "checkbox":
                //     return <></>; // TODO @Naru

                case "signature":
                    {
                        const signature = this.props.signatures.find(s => s.fieldIdx === idx);
                        if (!signature || !signature.data)
                            return <React.Fragment key={idx}></React.Fragment>;

                        return signature.data.signature ? <div
                            key={idx}
                            className="document-fillable-signature-signed"
                            style={{
                                ...style,
                                backgroundImage: `url('${signature.data.signature}')`
                            }}
                        /> : <div
                            key={idx}
                            className="document-fillable-signature-typed"
                            style={style}
                        >{String(signature.data.typed)}</div>;
                    }

                default: // Textbox
                    return( 
                    <>
                        <React.Fragment key={idx}>
                        <div
                            className="document-fillable-filled-textbox"
                            style={style}
                            title={f.dataType === "today" ? "" : "Click to edit value"}
                            onClick={() => {
                                this.props.jumpTo(idx, true);
                                this.setState({ fillableValue: value });
                            }}
                        >{value}</div>
                        {f.dataType !== "today" && (new URLSearchParams(window.location.search)).get('static') !== 'true' ? <div
                            className="document-fillable-filled-editable"
                            style={{
                                left: left + width + 4,
                                top: top + lh / 2 - 6
                            }}
                            title="Click to edit value"
                            onClick={() => {
                                this.props.jumpTo(idx, true);
                                this.setState({ fillableValue: value });
                            }}
                        /> : ""}
                        </React.Fragment>
                        {
                            idx===0 ?
                            this.state.showSubmit ?  
                                this.state.fillableValue ? 
                                    <div
                                        className="button"
                                        style={{
                                            paddingLeft: 20
                                        }}
                                        onClick={async () => {
                                            this.props.fill(f.key, value);
                                        }}
                                    >Submit </div> : "" 
                                :""
                            :""
                        }
                    </>)
                    
            }
        });

        
    }
    // renderFillablePublicBis = () => {
    //     if (!this.props.fillable || !this.props.metadata) return <></>;

    //     const pref = this.refs["page-" + this.props.pageIdx];
    //     if (!pref) return <></>;
    //     const page = pref.getBoundingClientRect();

    //     const m = this.props.metadata;

    //     return this.props.fillable.map((f, idx) => {
    //         if (!('x' in f) || !('y' in f) || !f.width || !f.height || f.page !== this.props.pageIdx) return <React.Fragment key={idx}></React.Fragment>;

    //         const left = page.width * f.x - 4;
    //         const top = page.height * f.y;
    //         const width = page.width * f.width + 4 - (this.props.stage === idx || ["signature (Mouse Only)", "signature", "today"].includes(f.dataType) ? 0 : 10);
    //         const height = page.height * f.height;

    //         // Dynamically calculate fontSize to force text to fit inside width
    //         let fontSize = Math.min(15 * this.props.zoom, height);
    //         if (f.dataType === "signature" && this.props.stage !== idx) {
    //             const signature = this.props.signatures.find(s => s.fieldIdx === idx);
    //             if (signature && signature.data.typed)
    //                 fontSize = Math.min(fontSize, width / signature.data.typed.length / 0.72);
    //         }

    //         let value = m[f.key] ? String(m[f.key]) : null;
    //         if (value && ["date", "today"].includes(f.dataType))
    //             value = formatDate(value, false, true);

    //         else if (value && value.length > 0 && f.dataType !== "signature")
    //             fontSize = Math.min(fontSize, width / value.length / 0.56);

    //         fontSize = Math.floor(fontSize);
    //         const lh = Math.max(fontSize, (2 * (height - fontSize / 2)));
    //         const lineHeight = lh + 'px';

    //         const style = { left, top, width, height, fontSize, lineHeight };

    //         // Does value exist?
    //         let input;
    //         if (!value && f.dataType !== "signature") {
    //             {
    //                 input = <input
    //                     type="text"
    //                     className="document-fillable-input"
    //                     id={"document-fillable-" + String(idx)}
    //                     style={{
    //                         ...style,
    //                         left: left + 5,
    //                         width: width - 10
    //                     }}
    //                     value={value}
    //                     // onChange={e => this.setState({ fillableValue: e.target.value })}

    //                     onClick={() => {
    //                         this.props.jumpTo(idx, true);
    //                         this.setState({ fillableValue: value });
    //                     }}
    //                     onKeyDown={(e) => {
    //                         if (e.key === "Enter") {
    //                             const value = e.target.value;
    //                             this.setState({ fillableValue: value });
    //                             if (this.props.stage < this.props.fillable.length - 1) {
    //                                 this.props.fill(f.key, value);
    //                             }
    //                         }
    //                     }}
    //                 />;
    //             }
    //             if (!value){
    //                 return <React.Fragment key={idx}>
    //                     {input}
    //                     <div
    //                         className="document-fillable-input"
    //                         style={{ top: top + height / 2 - 15 }}
    //                     />
    //                 </React.Fragment>;
    //             }

    //             if (this.props.stage === this.props.fillable.length - 1 && this.state.fillableValue){

    //                 return this.state.fillableValue ? <div
    //                     className="button"
    //                     style={{
    //                         float: 'none',
    //                         backgroundImage: "url('https://static.stax.ai/images/icons/send.png')"
    //                     }}
    //                     onClick={async () => {
    //                         this.props.fill(f.key, value);
    //                     }}
    //                 >Submit</div> : "";
    //             }
    //         }

    //         // // Else, display filled value
    //         switch (f.dataType) {
    //             // case "textarea":
    //             //     return <></>; // TODO @Naru

    //             // case "checkbox":
    //             //     return <></>; // TODO @Naru

    //             case "signature":
    //                 {
    //                     const signature = this.props.signatures.find(s => s.fieldIdx === idx);
    //                     if (!signature || !signature.data)
    //                         return <React.Fragment key={idx}></React.Fragment>;

    //                     return signature.data.signature ? <div
    //                         key={idx}
    //                         className="document-fillable-signature-signed"
    //                         style={{
    //                             ...style,
    //                             backgroundImage: `url('${signature.data.signature}')`
    //                         }}
    //                     /> : <div
    //                         key={idx}
    //                         className="document-fillable-signature-typed"
    //                         style={style}
    //                     >{String(signature.data.typed)}</div>;
    //                 }

    //             default: // Textbox
    //                 return <React.Fragment key={idx}>
    //                     <div
    //                         className="document-fillable-filled-textbox"
    //                         style={style}
    //                         title={f.dataType === "today" ? "" : "Click to edit value"}
    //                         onClick={() => {
    //                             this.props.jumpTo(idx, true);
    //                             this.setState({ fillableValue: value });
    //                         }}
    //                     >{value}</div>
    //                     {f.dataType !== "today" && (new URLSearchParams(window.location.search)).get('static') !== 'true' ? <div
    //                         className="document-fillable-filled-editable"
    //                         style={{
    //                             left: left + width + 4,
    //                             top: top + lh / 2 - 6
    //                         }}
    //                         title="Click to edit value"
    //                         onClick={() => {
    //                             this.props.jumpTo(idx, true);
    //                             this.setState({ fillableValue: value });
    //                         }}
    //                     /> : ""}
    //                 </React.Fragment>
    //         }
    //     });
    // }

    tagUser = (user) => {
        const content = this.state.replyContent;
        const tagCmps = content.split("@");
        let replyContent = tagCmps.slice(0, tagCmps.length - 1).join("@") + "<strong contenteditable=\"false\">" + user.name + "</strong> ";
        const reply = document.getElementById("comment-thread-reply");
        reply.innerHTML = replyContent;
        this.setState({
            replyContent,
            replyTags: [
                ...this.state.replyTags,
                user._id
            ]
        });
        reply.focus();
        if (typeof window.getSelection !== "undefined" && typeof document.createRange !== "undefined") {
            const range = document.createRange();
            range.selectNodeContents(reply);
            range.collapse(false);
            const sel = window.getSelection();
            sel.removeAllRanges();
            sel.addRange(range);
        } else if (typeof document.body.createTextRange !== "undefined") {
            const textRange = document.body.createTextRange();
            textRange.moveToElementText(reply);
            textRange.collapse(false);
            textRange.select();
        }
    }

    renderTagUserMenu = (users) => {
        if (!users || !users.length) return <></>;
        return <div className="comment-tag-menu">
            {users.map((user, idx) => user.name === "" ? <div key={idx} /> : <div
                key={idx}
                className="comment-tag-option"
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    this.tagUser(user);
                }}
            >{user.name}</div>)}
        </div>;
    }

    renderComments = (users) => {

        const user = this.props.user ? this.props.user : { name: "" };
        const userIdx = this.props.users.findIndex(u => u._id === user._id);

        return (<>
            {this.props.comments ? this.props.comments.map((c, idx) => {

                if (!c.thread)
                    return (<div key={idx}></div>);

                const thread = c.thread;

                let i = this.props.users.findIndex(u => u._id === (thread.length > 0 ? thread[0].user : user._id));
                const u = i >= 0 ? this.props.users[i] : { name: "Anonymous" };
                i -= userIdx;

                return (<div
                    key={idx}
                    id={"comment-" + c._id}
                    className={this.state.openComments[c._id] ? "comment-expanded" : "comment"}
                    title={this.state.openComments[c._id] ? "" : u.name + " - click to view"}
                    style={{
                        top: String(c.y * 100) + '%',
                        left: String(c.x * 100) + '%'
                    }}
                    onClick={() => {
                        if (!this.state.openComments[c._id])
                            this.setState({
                                openComments: {
                                    ...this.state.openComments,
                                    [c._id]: true
                                },
                                replyTo: null,
                                replyContent: ""
                            });
                    }}
                >
                    {!this.state.openComments[c._id] && <>
                        <div
                            className={"comment-user palette-color-" + String((i + 8000) % 8)}
                        >{u.name[0].toUpperCase()}</div>
                        <img
                            className="comment-icon"
                            src="https://static.stax.ai/images/icons/comment.png"
                            alt=""
                        />
                    </>}
                    {this.state.openComments[c._id] && <>
                        <img
                            className="comment-thread-action comment-thread-close"
                            src="https://static.stax.ai/images/icons/close.png"
                            alt="x"
                            title="Close comment(s)"
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                this.setState({
                                    openComments: {
                                        ...this.state.openComments,
                                        [c._id]: false
                                    },
                                    replyTo: null,
                                    replyContent: ""
                                })
                            }}
                        />
                        {(user.isAdmin || u._id === user._id) && <img
                            className="comment-thread-action comment-thread-delete"
                            src="https://static.stax.ai/images/icons/delete.png"
                            alt="Delete"
                            title="Delete comment(s)"
                            onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                this.props.deleteComment(c._id);
                            }}
                        />}
                        {thread.length > 0 ? thread.map((t, jdx) => {

                            let j = this.props.users.findIndex(u => u._id === t.user);
                            const v = j >= 0 ? this.props.users[j] : { name: "Anonymous" };
                            j -= userIdx;

                            return (<div
                                key={jdx}
                                className="comment-thread"
                                style={{
                                    marginLeft: v._id === u._id ? 13 : 0,
                                    marginRight: v._id === u._id ? 0 : 13,
                                    marginTop: jdx === 0 ? 20 : 10
                                }}
                            >
                                <div
                                    className={"comment-thread-user palette-color-" + String((j + 8000) % 8)}
                                    style={v._id === u._id ? {
                                        left: -16
                                    } : {
                                        right: -16
                                    }}
                                >{v.name[0].toUpperCase()}</div>
                                <div className="comment-thread-time">{formatDate(t.timestamp)}</div>
                                {renderHTML(t.text)}
                            </div>);
                        }) : <div style={{ clear: 'both', height: 20 }} />}
                        {/* Reply system */}
                        {this.state.replyTo === c._id ? <form
                            onSubmit={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const replyTo = this.state.replyTo;
                                const replyContent = this.state.replyContent;
                                const replyTags = [...this.state.replyTags];
                                let state = {
                                    replyTo: null,
                                    replyContent: "",
                                    replyTags: [],
                                };
                                // Close editor
                                if (this.props.newCommentId === replyTo)
                                    state.openComments = {
                                        ...this.state.openComments,
                                        [replyTo]: false
                                    };
                                this.setState(state);
                                this.props.replyToComment(replyTo, replyContent.replace(/ contenteditable="false"/g, ""), replyTags);
                            }}
                            className="comment-thread-container">
                            <div
                                id="comment-thread-reply"
                                className="comment-thread comment-thread-reply"
                                contentEditable
                                onInput={() => this.setState({
                                    replyContent: document.getElementById("comment-thread-reply").innerHTML
                                })}
                                onKeyDown={(e) => {
                                    if (e.key === "ArrowRight" || e.key === "Tab") {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        if (!users || !users.length)
                                            return;
                                        this.tagUser(users[0]);
                                    }
                                }}
                                placeholder="Type comment here..."
                                style={{
                                    marginTop: thread.length === 0 ? 20 : 10
                                }}
                            ></div>
                            <div className="comment-thread-time">Just now</div>
                            <div
                                className="comment-thread-user comment-thread-user-top palette-color-0"
                                style={{ left: -16 }}
                            >{user.name[0].toUpperCase()}</div>
                            {this.renderTagUserMenu(users)}
                            <input
                                type="submit"
                                value="Submit"
                                className="comment-reply-button comment-reply-submit"
                                title="Confirm reply"
                            />
                        </form> : <div
                            className="comment-reply-button"
                            title="Reply to comment"
                            onClick={(e) => {
                                this.setState({ replyTo: c._id, replyContent: "" });
                                setTimeout(() => {
                                    const input = document.getElementById('comment-thread-reply');
                                    if (input) input.focus();
                                }, 300);
                            }}
                        >Reply</div>}
                    </>}
                </div>);
            }) : <></>}
        </>);
    }

    // renderTableEditor() {
    //     const { table } = this.props; 
    //     if (Object.keys(table).length <= 0){
    //         return <></>;
    //     }
    //     return ( 
    //         <TableEditor 
    //             key={table._id + "-editor"} 
    //             table={table} 
    //             pageBounds={()=> this.refs["page-" + this.props.pageIdx].getBoundingClientRect()}
    //             {...this.props}
    //         />
    //     );

    // }

    setAspectRatio = () => {
        const box = this.refs["page-data-" + this.props.pageIdx];
        if (!box)
            return;

        const rect = box.getBoundingClientRect();
        // Based on rotation
        const aspectRatio = (this.props.rotation === undefined || this.props.rotation % 180 === 0) ? rect.height / rect.width : rect.width / rect.height;

        return this.setState({ aspectRatio });
    }

    getPageHeight = () => {
        let AR = this.props.aspectRatio ? this.props.aspectRatio : this.state.aspectRatio;

        if ([90, -90, 270].includes(this.props.rotation))
            AR = 1 / AR;

        return { height: "calc(" + (this.props.zoom * AR) + " * (78vh - 60px))" };
    }

    resetSelection = async () => {
        // If retagging field, then call the callback function!
        if (this.props.function === "retag-field" && this.props.callback && this.props.selection && this.props.selection.length)
            await this.props.callback(this.props.selection);

        this.mouseDown = false;
        this.setState({ selectionStart: null })
    }

    startSelection = (e) => {
        e.preventDefault();

        this.mouseDown = true;
        this.setState({ selectionStart: null })
        this.props.select([]);
    }

    render = () => {
        const user = this.props.user ? this.props.user : { name: "" };

        // Comments and tagging helpers...
        let filteredUsers = [];
        const content = this.state.replyContent;
        const tagCmps = content.split("@");
        if (tagCmps && tagCmps.length > 1) {
            const lastTag = tagCmps[tagCmps.length - 1];
            filteredUsers = this.props.users.filter(u => u.name.toLowerCase().includes(lastTag.toLowerCase()));
        }

        return (<React.Fragment key={this.props.pageIdx}>

            {/* Document splitter */}
            {!window.location.href.includes("public") && this.props.pageIdx > 0 && <PageSplit
                documentName={this.props.documentName}
                pageIdx={this.props.pageIdx}
                split={this.props.split}
                setSplit={this.props.setSplit}
                zoom={this.props.zoom}
            />}

            <div
                className={
                    "document-page" + 
                    (this.props.rotation ? " document-page-rotate-" + String(this.props.rotation) : "") + 
                    (this.props.function === 'splice-document' ? " document-page-splice" : "") +  
                    (this.props.functionData?.pages?.includes(this.props.pageIdx) ? " document-page-splice-selected" : "")
                }
                style={{
                    ...this.getPageHeight(),
                    ...(this.props.pageIdx === 0 ? { marginTop: 40 } : {}),
                    width: "calc(" + this.props.zoom + " * (78vh - 60px))",
                    cursor: this.props.tool === "Pencil" ? "url('https://static.stax.ai/images/tools/pencil-cursor.png') 0 32, auto" :
                        this.props.tool === "Eraser" ? "url('https://static.stax.ai/images/tools/eraser-cursor.png') 12 32, auto" :
                            this.props.tool === "Textbox" ? "text" :
                                this.props.tool === "Sign" ? "url('https://static.stax.ai/images/tools/signature-pen.png') 0 32, auto" :
                                    ["InsertBox", "InsertInput", "CopyBox"].includes(this.props.tool) ? "crosshair" :
                                        this.state.textHover ? "text" :
                                            "auto"
                }}
                key={this.props.pageIdx}
                id={"page-" + this.props.pageIdx}
                ref={"page-" + this.props.pageIdx}
                onClick={(e) => e.stopPropagation()}
                onDoubleClick={(e) => e.stopPropagation()}
                onContextMenu={this.props.onContextMenu}
            >

                {/* Page data (background image) */}
                {typeof this.props.pageData === "string" ? <img
                    id={"page-img-" + this.props.pageIdx}
                    ref={"page-data-" + this.props.pageIdx}
                    src={this.props.pageData}
                    style={{
                        [this.props.rotation && [90, 270, -90].includes(this.props.rotation) ? "height" : "width"]: "calc(" + this.props.zoom + " * (78vh - 60px))"
                    }}
                    alt={"Page " + this.props.pageIdx}
                    onMouseMove={(e) => {
                        this.overTextCursor(e)
                        this.highlightWords(e)
                    }}
                    onDoubleClick={this.highlightSingle}
                    onMouseDown={this.startSelection}
                    onMouseUp={this.resetSelection}
                    onLoad={this.setAspectRatio}
                    draggable={false}
                    
                /> : <div
                    ref={"page-data-" + this.props.pageIdx}
                    style={{
                        ...this.getPageHeight(),
                        width: "calc(" + this.props.zoom + " * (78vh - 60px))"
                    }}
                    className="document-loader-container"
                    title="Loading page..."
                    draggable={false}
                >
                    <div className="document-loader">
                        <Loading
                            type="bubbles"
                            color="var(--color-primary-dark)"
                            height={80}
                            width={80}
                        />
                        Loading...
                    </div>
                </div>}

                {/* Overlay canvas */}
                <Canvas
                    pageData={this.refs["page-data-" + this.props.pageIdx]}
                    pageIdx={this.props.pageIdx}
                    words={this.props.words}
                    signature={user.signature}
                    tool={this.props.tool}
                    callback={this.props.callback}
                    color={this.props.color}
                    setTool={this.props.setTool ? this.props.setTool : () => { }}
                    setCanvas={this.props.setCanvas ? this.props.setCanvas : () => { }}
                    save={(cvs) => {
                        if (!this.props.setCanvas || !this.props.saveCanvas) return;
                        this.props.setCanvas(cvs);
                        this.props.saveCanvas();
                    }}
                    zoom={this.props.zoom}
                    insertBound={this.insertBound}
                    pasteBound={this.pasteBound}
                    initialData={this.props.canvas}
                />

                {/* Highlight words */}
                {this.renderHighlightedWords()}

                {/* Search results */}
                {this.renderSearchResults()}

                {/* Table Editor */}
                {/* {this.renderTableEditor()} */}

                {/* PUBLIC ONLY */}
                {window.location.href.includes("/public/") ? <>
                    {/* Fillable fields */}
                    {this.renderFillablePublic()}
                    
                </> : <>
                    {/* PRIVATE ONLY */}
                    {/* Bounding boxes */}
                    {this.renderBoundingBoxes()}

                    {/* Hover over tag? */}
                    {this.renderHoveredElement(this.props.hovered)}

                    {/* Fillable inputs */}
                    {this.renderFillableInputs()}

                    {/* Comments */}
                    {this.renderComments(filteredUsers)}
                </>}

            </div>
        </React.Fragment>);
    }

};